// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { formatDistanceToNow } from "date-fns";
import { Calendar, Check, User, X } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useResolvePendingActionMutation } from "@/api/hooks/use-pending-actions";
import { KeyChip } from "@/components/shared/key-chip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RejectDialog } from "@/features/approvals/reject-dialog";
import { formatDateTime } from "@/lib/format";
import type { ActionPayload, ActionType, CreateChainPayload, PendingAction } from "@/lib/pending-actions/types";
import { useDateFormat } from "@/lib/preferences";

// ── Payload renderers ───────────────────────────────────────────────────────
// Add one entry per action type to display payload details on the card.

const ACTION_RENDERERS: Record<
  ActionType,
  (payload: ActionPayload, t: ReturnType<typeof useTranslation>["t"]) => ReactNode
> = {
  "create-chain": (payload, t) => {
    const p = payload as CreateChainPayload;
    return (
      <div className="flex flex-col gap-1 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t("chains.form.legibleName")}:</span>
          <span className="font-medium">{p.legibleName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t("chains.form.chainName")}:</span>
          <KeyChip label={p.chainName} variant="chain" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t("chains.form.defaultTimeZone")}:</span>
          <span>{p.defaultTimeZone}</span>
        </div>
        {p.copyFrom && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t("chains.form.copySettings")}:</span>
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{p.copyFrom}</code>
          </div>
        )}
      </div>
    );
  },
};

// ── Status badge ────────────────────────────────────────────────────────────

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pending: "outline",
  approved: "default",
  rejected: "destructive",
  executed: "secondary",
  failed: "destructive",
};

// ── Component ───────────────────────────────────────────────────────────────

interface ActionCardProps {
  action: PendingAction;
  isAdmin: boolean;
}

export function ActionCard({ action, isAdmin }: ActionCardProps) {
  const { t } = useTranslation("back-office");
  const [dateFormat] = useDateFormat();
  const resolveMutation = useResolvePendingActionMutation();
  const [rejectOpen, setRejectOpen] = useState(false);

  const renderer = ACTION_RENDERERS[action.actionType];
  const isPending = action.status === "pending";

  function handleApprove() {
    resolveMutation.mutate({ id: action.id, status: "approved" });
  }

  function handleReject(reason?: string) {
    resolveMutation.mutate({ id: action.id, status: "rejected", rejectionReason: reason });
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_VARIANTS[action.status] ?? "outline"}>
              {t(`approvals.status.${action.status}`)}
            </Badge>
            <Badge variant="secondary">{t(`approvals.actionTypes.${action.actionType}`)}</Badge>
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(action.createdAt), { addSuffix: true })}
          </span>
        </div>

        {/* Payload details */}
        {renderer?.(action.payload, t)}

        {/* Metadata */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <User className="h-3 w-3" />
            {action.requester.fullName}
          </span>
          {action.scheduledFor && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {t("approvals.scheduledFor", {
                time: formatDateTime(action.scheduledFor, dateFormat),
              })}
            </span>
          )}
          {action.resolvedBy && (
            <span className="inline-flex items-center gap-1">
              <Check className="h-3 w-3" />
              {t("approvals.resolvedBy", {
                action: t(`approvals.status.${action.status}`),
                name: action.resolvedBy.fullName,
              })}
            </span>
          )}
        </div>

        {/* Requester message */}
        {action.requesterMessage && (
          <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground italic">
            {action.requesterMessage}
          </div>
        )}

        {/* Rejection reason */}
        {action.rejectionReason && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {action.rejectionReason}
          </div>
        )}

        {/* Admin actions */}
        {isPending && isAdmin && (
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={handleApprove} disabled={resolveMutation.isPending}>
              <Check className="h-3.5 w-3.5" />
              {t("approvals.actions.approve")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRejectOpen(true)}
              disabled={resolveMutation.isPending}
            >
              <X className="h-3.5 w-3.5" />
              {t("approvals.actions.reject")}
            </Button>
          </div>
        )}

        <RejectDialog
          open={rejectOpen}
          onOpenChange={setRejectOpen}
          onReject={(reason) => {
            handleReject(reason);
            setRejectOpen(false);
          }}
        />
      </CardContent>
    </Card>
  );
}
